/**
 * BCR Estimate card — Cost / Bid / Margin + Cost Model / Pricing / Approval / Submission Basis.
 */
import React from 'react';
import { Card, Col, Row, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { epcCommercialService } from '@/services/epcCommercial.service';

const { Text } = Typography;

export const EpcBcrEstimateStatusCard: React.FC<{
  tenderId: string;
  proposal: Record<string, unknown> | null;
}> = ({ tenderId, proposal }) => {
  const listQ = useQuery({
    queryKey: ['epc-est-list', tenderId],
    queryFn: () => epcEstimationService.list(tenderId),
    enabled: !!tenderId,
  });
  const estimateId = listQ.data?.[0]?.id ? String(listQ.data[0].id) : '';

  const readinessQ = useQuery({
    queryKey: ['epc-est-readiness', estimateId],
    queryFn: () => epcEstimationService.readiness(estimateId),
    enabled: !!estimateId,
  });

  const pricingQ = useQuery({
    queryKey: ['epc-est-pricing-summary', estimateId],
    queryFn: async () => {
      const ws = await epcEstimationService.getWorkspace(estimateId);
      const revId = String((ws as any)?.currentRevision?.id || '');
      if (!revId) return null;
      return epcEstimationService.pricingSummary(estimateId, revId);
    },
    enabled: !!estimateId,
  });

  const proposalId = proposal ? String(proposal.id) : '';
  const basisQ = useQuery({
    queryKey: ['epc-proposal-submission-basis', proposalId],
    queryFn: () => epcCommercialService.getProposalSubmissionBasis(proposalId),
    enabled: !!proposalId,
  });

  const r = readinessQ.data as any;
  const p = (pricingQ.data as any)?.pricing || pricingQ.data;
  const cost = Number((pricingQ.data as any)?.total_estimated_cost ?? p?.cost_basis_snapshot) || null;
  const bid = p?.final_bid_price != null ? Number(p.final_bid_price) : null;
  const margin = p?.margin_actual != null ? Number(p.margin_actual) : null;
  const basis = String((basisQ.data as any)?.status || 'NOT_RECORDED');
  const revStatus = String(r?.revision_status || '—');

  return (
    <Card size="small" title="EST3 Estimate status">
      <Row gutter={[16, 8]}>
        <Col xs={12} md={6}>
          <Text type="secondary">Total estimated cost</Text>
          <div>{cost != null ? formatMoney(cost) : '—'}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Final bid</Text>
          <div>{bid != null ? formatMoney(bid) : '—'}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Margin %</Text>
          <div>{margin != null ? `${margin}` : '—'}</div>
        </Col>
        <Col xs={12} md={6}>
          <Text type="secondary">Revision</Text>
          <div>{r?.revision_label || '—'} · {revStatus}</div>
        </Col>
      </Row>
      <Space wrap style={{ marginTop: 12 }}>
        <Tag color={r?.cost_model === 'READY' ? 'green' : 'orange'}>
          Cost model {r?.cost_model || '…'}
        </Tag>
        <Tag color={r?.commercial_pricing === 'READY' ? 'green' : 'orange'}>
          Pricing {r?.commercial_pricing || '…'}
        </Tag>
        <Tag color={['Approved', 'ApprovalPending'].includes(revStatus) ? 'green' : 'default'}>
          Approval {revStatus}
        </Tag>
        <Tag color={basis === 'CERTIFIED' ? 'green' : basis === 'STALE' ? 'red' : 'orange'}>
          Submission basis {basis === 'NOT_RECORDED' ? 'NOT RECORDED' : basis}
        </Tag>
      </Space>
    </Card>
  );
};
